import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, Moon } from "lucide-react";
import { api } from "@/lib/api";
import { signed } from "@/lib/share";

const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const tone = (n) => (n > 0 ? "text-[#B7F52A]" : n < 0 ? "text-rose-300" : "text-slate-300");

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-xl bg-white/[0.04] p-3 text-center">
      <p className={`font-mono-num text-2xl font-bold ${tone(value)}`} data-testid={testId}>{signed(value)}</p>
      <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
    </div>
  );
}

function GameRow({ g }) {
  const [open, setOpen] = useState(false);
  const pickAbbr = g.pick ? g[g.pick] : null;
  return (
    <li className="border-b border-white/[0.05] last:border-0" data-testid={`myday-game-${g.game_id}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center gap-3 py-2.5 text-left" data-testid={`myday-toggle-${g.game_id}`}>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{g.away} {g.away_score ?? ""} – {g.home_score ?? ""} {g.home}</span>
          <span className="block text-xs text-slate-400" data-testid={`myday-pick-${g.game_id}`}>{pickAbbr ? `Ton prono : ${pickAbbr}` : "Aucun choix de vainqueur"}
            {!pickAbbr && g.lines?.some((l) => l.points) && <span className="text-slate-500"> · {g.lines.filter((l) => l.points).map((l) => `${l.label} : ${signed(l.points)}`).join(" · ")}</span>}
          </span>
        </span>
        <span className={`font-mono-num text-sm font-bold ${tone(g.total)}`}>{signed(g.total)}</span>
        <ChevronDown size={15} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="mb-2 space-y-1 rounded-lg bg-white/[0.03] p-2" data-testid={`myday-lines-${g.game_id}`}>
          {g.lines.map((l, i) => (
            <li key={i} className="flex justify-between gap-3 text-xs"><span className="text-slate-300">{l.label}</span><span className={`font-mono-num font-bold ${tone(l.points)}`}>{signed(l.points)}</span></li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function MyDayCard({ leagueId }) {
  const [day, setDay] = useState(null);
  const [d, setD] = useState(null);
  useEffect(() => { setDay(null); }, [leagueId]);
  useEffect(() => {
    api.get(`/leagues/${leagueId}/my-day`, { params: day ? { day } : {} }).then((r) => setD(r.data)).catch(() => setD(null));
  }, [leagueId, day]);
  if (!d?.day) return null;
  return (
    <section className="mpl-card p-5" data-testid="myday-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="min-w-0 text-sm font-bold"><span className="flex items-center gap-2"><Moon size={15} className="shrink-0 text-[#B7F52A]" /> Ma soirée</span>
          <span className="mt-0.5 block text-xs font-semibold text-slate-400 first-letter:uppercase" data-testid="myday-date">{fmtDay(d.day)}</span></h2>
        <div className="flex shrink-0 gap-1">
          <button onClick={() => setDay(d.prev)} disabled={!d.prev} aria-label="Jour précédent" className="mpl-btn-ghost px-2 py-1 disabled:opacity-30" data-testid="myday-prev"><ChevronLeft size={15} /></button>
          <button onClick={() => setDay(d.next)} disabled={!d.next} aria-label="Jour suivant" className="mpl-btn-ghost px-2 py-1 disabled:opacity-30" data-testid="myday-next"><ChevronRight size={15} /></button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Gagnés" value={d.won} testId="myday-won" />
        <Stat label="Perdus" value={d.lost} testId="myday-lost" />
        <Stat label="Bilan" value={d.diff} testId="myday-diff" />
      </div>
      <ul className="mt-3" data-testid="myday-games">{d.games.map((g) => <GameRow key={g.game_id} g={g} />)}</ul>
      <p className="mt-2 text-[11px] text-slate-500">Touche un match pour voir le détail des points.</p>
    </section>
  );
}
